import { faPencil, faRotateLeft } from '@fortawesome/free-solid-svg-icons';

import Badge from '@/components/Badge';
import IconicButton from '@/components/IconicButton';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TableRow, TableCell } from '@/elements/Table';

import { Certificate } from './api';

type Props = {
  certificate: Certificate;
  onEdit: (certificate: Certificate) => void;
  onRestore: (certificate: Certificate) => void;
};

const usageLabel = (certificate: Certificate) => {
  const { usage } = certificate;
  const parts: string[] = [];
  if (usage.workers > 0) {
    parts.push(`${usage.workers} worker${usage.workers === 1 ? '' : 's'}`);
  }
  if (usage.customers > 0) {
    parts.push(`${usage.customers} client${usage.customers === 1 ? '' : 's'}`);
  }
  if (usage.upcoming_jobs > 0) {
    parts.push(
      `${usage.upcoming_jobs} upcoming job${
        usage.upcoming_jobs === 1 ? '' : 's'
      }`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'Not in use';
};

const BadgeRowItem = ({ certificate, onEdit, onRestore }: Props) => {
  const isBadge = certificate.certificate_type === 'BADGE';

  return (
    <TableRow>
      <TableCell>
        {certificate.image_url ? (
          <img
            alt=""
            src={certificate.image_url}
            style={{ width: 32, height: 32, objectFit: 'contain' }}
          />
        ) : (
          <Small color="lighter">—</Small>
        )}
      </TableCell>
      <TableCell>
        <TextStack>
          <Body weight="medium">{certificate.name}</Body>
          {certificate.display_name_es && (
            <Small color="light">{certificate.display_name_es}</Small>
          )}
        </TextStack>
      </TableCell>
      <TableCell>
        <Badge
          label={isBadge ? 'Badge' : 'Certificate'}
          status={isBadge ? 'theme' : 'neutral'}
        />
      </TableCell>
      <TableCell>
        <Small color={certificate.usage.workers > 0 ? 'default' : 'lighter'}>
          {usageLabel(certificate)}
        </Small>
      </TableCell>
      <TableCell>
        {certificate.deleted ? (
          <Badge label="Deleted" status="danger" />
        ) : (
          <Badge label="Active" status="success" />
        )}
      </TableCell>
      <TableCell>
        <Stack gap={4} justify="end">
          {/* GRAVY-10787: a deleted row is read-only. Offering Edit here let an
              admin silently rename a record the list marks as removed, which
              workers may still hold. Restore first, then edit. */}
          {certificate.deleted ? (
            <IconicButton
              a11yLabel={`Restore ${certificate.name}`}
              appearance="plain"
              icon={faRotateLeft}
              size="sm"
              onClick={() => onRestore(certificate)}
            />
          ) : (
            /* GRAVY-10807: no Delete. Retiring a badge is a manual,
               engineer-run operation now — the server route is gone. The
               in-use guard behind it never covered jobs already in progress,
               so a badge actively gating a live shift could be retired with no
               refusal, then keep gating it while invisible in this catalog. */
            <IconicButton
              a11yLabel={`Edit ${certificate.name}`}
              appearance="plain"
              icon={faPencil}
              size="sm"
              onClick={() => onEdit(certificate)}
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default BadgeRowItem;
